import { useEffect, useState } from 'react';
import { withDoc } from './promptPictures';
import { partDbSize } from './partIdentity';
import type { CatalogEntry } from './catalogItems';
import type { PartItem, PartSize } from './partsRun';
import type { BySchemaPart, SchemaCrop } from './schemaCropsRun';

// HOW BIG A TOOL IS, worked out from the drawings it appears in.
//
// A tool the carton does not contain has no size and never will: it is not in the
// supplied-parts section, so nothing measured it there, and the Parts-DB has no row for a
// screwdriver somebody already owns. The 3D viewer then has a mesh it cannot anchor, and
// Phase 5 draws it at whatever size the file happened to be.
//
// But the manual DID draw it — beside the parts it is working on, at the same scale, on the
// same page. And those parts are measured. So the size is already on the page as a ratio:
// the barrel nut in that drawing is 15 mm and 0.021 of the page wide, which makes the page
// 714 mm wide at that spot, which makes the screwdriver beside it 182 mm long.
//
// NO CALL IS MADE. Every input exists: the boxes come from the manual search (schemaCropsRun),
// which pairs of them belong together comes from that search's `touches`, and the reference
// sizes come from Phase 2. This is arithmetic on answers already paid for — see
// sizeFromScale, which does the same thing to listing photographs and is where the shape of
// this was taken from.

// One reading: one drawing of the tool, measured against one drawing of one part it is
// working on there.
export type ToolRead = {
  step: number;
  page: number;
  // The part that gave the scale, and the dimension of it that was used.
  ref: number;
  refMm: number;
  // Both boxes' long side, in PAGE-WIDTH units — the y edge already multiplied by the page's
  // aspect, so the two numbers are comparable lengths and not two different fractions.
  refSide: number;
  toolSide: number;
  // What the page measures at, there, and what that makes the tool.
  mmPerUnit: number;
  lengthMm: number;
};

// THE LONGEST DIMENSION A PART IS KNOWN TO HAVE, which is the one a bounding box can be
// matched against: a box's long side is the part's long side, whichever way round the
// drawing put it. A diameter is deliberately not in here — it is the SHORT axis of every
// part that has one, and reading it against the long side of a box would size a screw by
// its thickness.
export const longestKnown = (size: PartSize | null | undefined): number | null => {
  const got = [size?.x, size?.y, size?.z, size?.lengthMm]
    .filter((v): v is number => typeof v === 'number' && v > 0);
  return got.length ? Math.max(...got) : null;
};

// A box's two sides as LENGTHS. x is a fraction of the page's width and y a fraction of its
// height, and on a page half as tall again as it is wide those two fractions are not the
// same distance — comparing them raw is a 40% error waiting for a part drawn upright.
//
// The BOX, and not the part's own rectangle. A part drawn at an angle fills more box than it
// is long, so a ratio of two boxes carries whatever the two angles differ by — which is a
// real error and was worth trying to remove. Measuring it off the ink was tried and taken
// out again: a line drawing is not one connected mark, so the shape a threshold finds is a
// fragment of a screwdriver as often as it is the screwdriver, and a fragment measures
// beautifully. An over-estimate you can reason about beat a measurement that was sometimes
// a third of the answer.
const sidesOf = (box: [number, number, number, number], aspect: number) => {
  const w = Math.abs(box[2] - box[0]);
  const h = Math.abs(box[3] - box[1]) * aspect;
  return { long: Math.max(w, h), short: Math.min(w, h) };
};

// HOW FAR APART TWO BOXES ARE, in page-width units: the gap between their edges, zero where
// they overlap. Centre distance would be wrong for exactly the pair this has to judge — a
// screwdriver is long and the screw it is driving is at one end of it, so its centre is
// further from that screw than from a screw it is not touching at all.
const gapBetween = (
  a: [number, number, number, number], b: [number, number, number, number], aspect: number,
) => {
  const dx = Math.max(0, a[0] - b[2], b[0] - a[2]);
  const dy = Math.max(0, a[1] - b[3], b[1] - a[3]) * aspect;
  return Math.hypot(dx, dy);
};

// THE ONE INSTANCE THE TOOL IS ON, out of everything it touches in that drawing.
//
// A step draws the same screw four times going into four holes, and the tool is in exactly
// one of them: the others are the same part, in the same step, doing the same job without
// it. Drawn, all four say "this is what the screwdriver is on" and three of them are wrong;
// measured, the three are rulers whose distance from the tool is the only thing that makes
// them wrong — the ratio comes out fine, so a bad pairing is invisible in the number.
//
// So: the nearest, by the gap between the rectangles. A tool held against a part overlaps it
// or nearly does, which no other instance on the page does.
export function nearestTouch(
  crop: SchemaCrop, byPart: BySchemaPart, aspect: number,
): { at: number; crop: SchemaCrop } | null {
  let best: { at: number; crop: SchemaCrop; gap: number } | null = null;
  for (const at of crop.touches ?? []) {
    for (const other of byPart[at] ?? []) {
      if (other.page !== crop.page || other.step !== crop.step) continue;
      const gap = gapBetween(crop.bbox, other.bbox, aspect);
      if (!best || gap < best.gap) best = { at, crop: other, gap };
    }
  }
  return best ? { at: best.at, crop: best.crop } : null;
}

// EVERY READING FOR ONE TOOL. One per (drawing of the tool × part it touches there ×
// drawing of that part in the same step and page) — the last of those because a step can
// draw a screw four times going into four holes, and each of them is a ruler.
export function toolReads(
  tool: number,
  items: PartItem[],
  catalog: CatalogEntry[],
  byPart: BySchemaPart,
  aspects: Record<number, number>,
  // THE RULER'S BEST NUMBER, by catalog item: the longest side of that part's 3D model, in
  // millimetres. Preferred over anything the manual printed, and for a fastener it is often
  // the only thing there is — "M6, slotted drive" is a designation, not a length. See
  // meshDims, which measures the mesh the viewer has been labelling all along.
  meshLongest?: Map<number, number>,
): ToolRead[] {
  const out: ToolRead[] = [];
  const sizeOf = (at: number): PartSize | null =>
    partDbSize(catalog, items[at]?.catalogId ?? null, items[at]?.size ?? null);
  const knownOf = (at: number): number | null => {
    const itemId = items[at]?.catalogId;
    const fromMesh = itemId == null ? undefined : meshLongest?.get(itemId);
    return fromMesh ?? longestKnown(sizeOf(at));
  };

  for (const crop of byPart[tool] ?? []) {
    if (!crop.touches?.length) continue;
    const aspect = aspects[crop.page];
    if (!aspect) continue;
    const toolSide = sidesOf(crop.bbox, aspect).long;
    if (toolSide <= 0) continue;

    // ONE RULER PER DRAWING: the instance the tool is actually on — see nearestTouch.
    const near = nearestTouch(crop, byPart, aspect);
    if (!near) continue;
    const refMm = knownOf(near.at);
    if (refMm == null) continue;
    const refSide = sidesOf(near.crop.bbox, aspect).long;
    if (refSide <= 0) continue;
    const mmPerUnit = refMm / refSide;
    out.push({
      step: crop.step,
      page: crop.page,
      ref: near.at,
      refMm,
      refSide,
      toolSide,
      mmPerUnit,
      lengthMm: toolSide * mmPerUnit,
    });
  }
  return out;
}

// THE MEDIAN, and not the mean or the biggest.
//
// Every reading here is a ratio of two hand-drawn rectangles, and the way they go wrong is
// one bad box rather than a spread: a screw boxed with its callout arrow is half as long
// again as it is, and one of those pulls a mean by as much as it is wrong. The median is
// unmoved by it as long as most of the boxes are right, which is the case this is for.
export function toolLength(reads: ToolRead[]): number | null {
  if (!reads.length) return null;
  const got = reads.map((r) => r.lengthMm).sort((a, b) => a - b);
  const mid = Math.floor(got.length / 2);
  return got.length % 2 ? got[mid] : (got[mid - 1] + got[mid]) / 2;
}

// THE SHAPE OF EVERY PAGE A CROP SITS ON — height over width, which is all the arithmetic
// above needs of it. Read from the PDF rather than assumed A4: a manual printed landscape
// would size every tool by the ratio of the two.
export function usePageAspects(file: Blob, pages: number[]): Record<number, number> {
  const [aspects, setAspects] = useState<Record<number, number>>({});
  const key = [...new Set(pages)].sort((a, b) => a - b).join(',');
  useEffect(() => {
    let cancelled = false;
    const wanted = key ? key.split(',').map(Number) : [];
    if (!wanted.length) {
      setAspects({});
      return () => { cancelled = true; };
    }
    void withDoc(file, async (doc) => {
      const got: Record<number, number> = {};
      for (const n of wanted) {
        try {
          const view = (await doc.getPage(n)).getViewport({ scale: 1 });
          if (view.width > 0) got[n] = view.height / view.width;
        } catch {
          // A page the document does not have: the crop filed under it is unusable and
          // says so by having no aspect, which is one missing reading and not a failure.
        }
      }
      if (!cancelled) setAspects(got);
      return null;
    }, null);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, key]);
  return aspects;
}

// Which pages the crops of interest are on — what usePageAspects has to be given.
export const cropPages = (byPart: BySchemaPart, of: number[]): number[] =>
  of.flatMap((at) => (byPart[at] ?? []).map((c: SchemaCrop) => c.page));
